import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { KeyRound, Building2, ReceiptText, Truck, Plus, Trash2, ShieldCheck,
         SlidersHorizontal, LayoutList, Send, HelpCircle, Activity, ScrollText } from "lucide-react";
import { clearSiteConfigCache } from "@/lib/siteConfig";
import { PAGE_SIZE_CHOICES } from "@/components/Pagination";
import { RANGE_DAYS_CHOICES } from "@/components/DateRange";

/**
 * What the health check's `reason` means, in the admin's terms.
 *
 * Three different problems look identical from the chat (no message arrived), so each gets its own
 * sentence saying where to go: the environment, this page, or Telegram itself. Mirrors the reasons
 * `core.health_report.check_now` returns.
 */
const HEALTH_OUTCOMES = {
  sent: "Sent — check the chat.",
  not_configured: "Not sent: this deployment has no bot token or chat id. "
                + "TELEGRAM_BOT_TOKEN and TELEGRAM_CHAT_ID are environment variables — see deploy/TELEGRAM.md.",
  muted: "Not sent: alerts are switched off above. Turn 'Alert on payment' back on to use the chat.",
  failed: "Not sent: Telegram refused the message. Usually a wrong chat id, or the bot was removed "
        + "from the group. The backend log has the error (event: telegram).",
};

const Panel = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-[#111623] border border-white/10 rounded-2xl p-6 space-y-4">
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-[#CBA96C] mt-1 shrink-0" strokeWidth={1.5}/>
      <div>
        <h2 className="font-serif-display text-lg text-white">{title}</h2>
        {desc && <p className="text-xs text-[#6B7280] mt-0.5">{desc}</p>}
      </div>
    </div>
    {children}
  </div>
);

/**
 * The explanation for one setting, behind a question mark.
 *
 * This page had grown paragraphs under every control — accurate, and nobody reads a settings page
 * to read. The reasoning now lives one hover away, and the page itself stays scannable: a label, a
 * control, and a `?` for the day you need it.
 *
 * A `<button>` trigger, not a bare icon, because a tooltip only reachable by mouse is not reachable
 * at all for anyone on a keyboard — Radix opens this on focus as well as hover, and closes it on
 * Escape. `type="button"` matters: these sit inside the settings form and the default would submit
 * it. Deep explanations still belong in the docs; this is for the sentence that saves a trip there.
 */
const Help = ({ children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" aria-label="What is this?"
              className="text-[#6B7280] hover:text-[#CBA96C] transition-colors align-middle ml-1.5">
        <HelpCircle className="w-3.5 h-3.5 inline" strokeWidth={1.75}/>
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" align="start"
                    className="max-w-xs bg-[#111623] border border-white/15 text-[#D6DCE8] text-[11px] leading-relaxed px-3 py-2">
      {children}
    </TooltipContent>
  </Tooltip>
);

/**
 * A label with its explanation tucked behind a `?`, so every setting reads the same way.
 *
 * `required` draws the gold `*`. Most settings on this page fall back to a default when left
 * blank, which is why the marker is worth something here: it distinguishes the handful that have
 * no sensible default — Company details — from the majority that do.
 */
const SettingLabel = ({ label, hint, required }) => (
  <Label className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">
    {label}{required && <span className="text-[#CBA96C] ml-0.5" aria-hidden="true">*</span>}
    {hint && <Help>{hint}</Help>}
  </Label>
);

const Field = ({ label, hint, required, ...props }) => (
  <div>
    <SettingLabel label={label} hint={hint} required={required}/>
    <Input {...props} aria-required={required || undefined}
           className="mt-1.5 bg-[#080C16] border-white/10 text-white"/>
  </div>
);

const AreaField = ({ label, hint, required, ...props }) => (
  <div>
    <SettingLabel label={label} hint={hint} required={required}/>
    <Textarea {...props} aria-required={required || undefined}
              className="mt-1.5 bg-[#080C16] border-white/10 text-white min-h-[76px]"/>
  </div>
);

export function AdminSettings() {
  const [site, setSite] = useState(null);
  const [bill, setBill] = useState(null);
  const [billLimits, setBillLimits] = useState({});
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  // The on-demand health check: whether one is in flight, and the last result.
  const [checking, setChecking] = useState(false);
  const [health, setHealth] = useState(null);

  // The **admin** settings route, not the public `/site-config`. The document password is
  // deliberately absent from the public one (SITE_PRIVATE_KEYS in routers/public.py), which is
  // fetched by every anonymous visitor — so reading it here is the only way this page can show
  // the admin a value they are meant to be able to change. Falls back to the public route so the
  // page still renders its other settings if this one ever fails.
  useEffect(() => {
    api.get("/admin/site-config")
      .then(r => setSite(r.data || {}))
      .catch(() => api.get("/site-config").then(r => setSite(r.data || {})).catch(() => setSite({})));
  }, []);
  useEffect(() => {
    api.get("/admin/bill-template")
      .then(r => { setBill(r.data.template || {}); setBillLimits(r.data.limits || {}); })
      .catch(() => setBill({}));
  }, []);

  // Delivery partners: courier name, contact, and a tracking-URL template — see
  // core/delivery_partners.py. Edited as a plain local list and saved wholesale, same shape as
  // the Template Catalog's group editor (PUT /admin/template-groups): a handful of hand-curated
  // rows, not something that needs per-row network round-trips.
  const [partners, setPartners] = useState(null);
  const [partnersBusy, setPartnersBusy] = useState(false);
  useEffect(() => { api.get("/admin/delivery-partners").then(r => setPartners(r.data || [])).catch(() => setPartners([])); }, []);
  const addPartner = () => setPartners(p => [...p, { id: null, name: "", contact: "", url_template: "" }]);
  const updatePartner = (i, patch) => setPartners(p => p.map((row, idx) => idx === i ? { ...row, ...patch } : row));
  const removePartner = (i) => setPartners(p => p.filter((_, idx) => idx !== i));
  const savePartners = async () => {
    setPartnersBusy(true);
    try {
      const r = await api.put("/admin/delivery-partners", partners);
      setPartners(r.data);
      toast.success("Delivery partners saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setPartnersBusy(false); }
  };

  /**
   * The backend's log level — `GET`/`PUT /admin/log-level`, not site config.
   *
   * Applied to the running process the moment it is saved, with no restart, and **deliberately not
   * stored**: a restart returns to `LOG_LEVEL` from the environment (INFO by default), so a DEBUG
   * switched on during an incident cannot be forgotten and left on for a month. That is also why
   * this card has its own Save rather than the shared SaveBar — it is not part of the `site` record.
   * See LOGGING.md.
   */
  const [logInfo, setLogInfo] = useState(null);
  const [logLevel, setLogLevel] = useState("INFO");
  const [logBusy, setLogBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/log-level")
      .then(r => { setLogInfo(r.data); setLogLevel(r.data?.current?.dharmatn || r.data?.default || "INFO"); })
      .catch(() => setLogInfo({}));
  }, []);
  const saveLogLevel = async () => {
    setLogBusy(true);
    try {
      const r = await api.put("/admin/log-level", { level: logLevel });
      setLogInfo(i => ({ ...i, current: r.data.current }));
      toast.success(`Log level set to ${logLevel} — applied now`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change the log level"); }
    finally { setLogBusy(false); }
  };

  /**
   * Send a health report to Telegram now.
   *
   * The route answers 200 with a `reason` even when nothing was sent, because "not sent" is the
   * answer to the question being asked rather than a failed request — so only a genuine transport
   * failure lands in `catch`. See `POST /admin/telegram/health-report`.
   */
  const runHealthCheck = async () => {
    setChecking(true);
    setHealth(null);
    try {
      const r = await api.post("/admin/telegram/health-report");
      setHealth(r.data);
      if (r.data?.sent) toast.success("Health report sent to Telegram");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run the health check");
    } finally { setChecking(false); }
  };

  /**
   * GST on/off.
   *
   * **There is no stored `gst_enabled` flag, and deliberately so.** The GSTIN is the switch
   * (core.templates_data.gst_enabled): set means registered and charging, blank means not. A
   * second boolean beside it could disagree, and the disagreeing state — "charging GST" with no
   * registration number — is exactly what an invoice must never print. So this toggle is a view
   * over one field: ON reveals the input, OFF clears it.
   *
   * `gstOn` is separate component state rather than `Boolean(bill.gstin)` because switching ON has
   * to show an *empty* required field before anything is typed. Save is refused in that state, so
   * the invalid combination cannot be persisted even for a moment.
   */
  const [gstOn, setGstOn] = useState(false);
  useEffect(() => { if (bill) setGstOn(Boolean(String(bill.gstin || "").trim())); }, [bill?.gstin !== undefined]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGst = (on) => {
    if (on) { setGstOn(true); return; }
    // Turning it off clears the number. Confirmed, because retyping a GSTIN is a trip to find the
    // certificate — and because this silently changes what every future customer is charged.
    if (!window.confirm(
      "Turn GST off?\n\nThe GSTIN will be cleared, and prices will stop including GST across the "
      + "price list, cart, payment and invoice. You will need to re-enter the number to turn it "
      + "back on.")) return;
    setGstOn(false);
    setBill(b => ({ ...b, gstin: "" }));
  };

  const saveBill = async () => {
    // The rule, enforced before the request rather than after: registered means a number. The
    // server cannot check this on its own — it only ever receives a GSTIN or an empty string, and
    // an empty one is the legitimate way to say "not registered".
    if (gstOn && !String(bill.gstin || "").trim()) {
      toast.error("Enter your GSTIN, or turn GST off. An invoice cannot claim tax without a registration number.");
      return;
    }
    setBusy(true);
    try {
      // Only the invoice's own fields. `bill` carries the merged company identity for display, and
      // the endpoint refuses those keys — they belong to Company details.
      const { tagline, title, gstin, footer } = bill;
      await api.put("/admin/bill-template", { tagline, title, gstin, footer });
      // No cache to clear: the invoice is rendered server-side at payment, so the next bill picks
      // this up with nothing else to invalidate.
      toast.success("Invoice template saved — applies to invoices generated from now on");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  // The four cards below are different *concerns*, not different records: they all edit one
  // `site` config object and one endpoint writes it. So each card carries this same control, and
  // saving from any of them commits every pending edit rather than only that card's — which is
  // what the single button at the bottom of one giant card used to do anyway. Splitting the cards
  // was about not making an admin read company details to find the document password; it was not
  // a reason to split the record into four saves that could each clobber the others.
  const SaveBar = ({ id }) => (
    <Button data-testid={`settings-save-${id}`} disabled={busy} onClick={saveSite}
            className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
      {busy ? "Saving…" : "Save settings"}
    </Button>
  );

  // Company details have no defaults to fall back on — the footer on every page, the privacy
  // policy's data-fiduciary clause and the invoice's FROM block all render them — so a save that
  // blanks one is refused. **The server is the enforcement** (`COMPANY_DETAILS` in
  // routers/admin.py); this mirror exists so the page says which field is missing without a round
  // trip, and because any card's Save sends the whole record, so the message has to name the card.
  const companyProblem = () => {
    const required = [
      ["company_name", "Company name"],
      ["address", "Registered address"],
      ["support_email", "Business email"],
      ["whatsapp", "WhatsApp Business Number"],
    ];
    for (const [key, label] of required) {
      if (!String(site?.[key] ?? "").trim()) return `${label} is required under Company details.`;
    }
    if (!/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(String(site.support_email).trim())) {
      return "Business email does not look like an email address.";
    }
    if (!/^[0-9]{10,15}$/.test(String(site.whatsapp).replace(/[^0-9]/g, ""))) {
      return "WhatsApp Business Number must be 10 to 15 digits, including the country code.";
    }
    return null;
  };

  const saveSite = async () => {
    const problem = companyProblem();
    if (problem) { toast.error(problem); return; }
    setBusy(true);
    try {
      await api.put("/admin/site-config", site);
      // These two drive the footer on every page, and `useSiteConfig` caches per page load —
      // without this, admin saves a new support address and keeps seeing the old one until they
      // reload, which reads as the save having failed.
      clearSiteConfigCache();
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.put("/auth/password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change password"); }
    finally { setBusy(false); }
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6 max-w-2xl" data-testid="admin-settings-page">
      <h1 className="font-serif-display text-3xl md:text-4xl text-white">Settings</h1>

      <Panel icon={Building2} title="Company details"
             desc="Who this business is. All four are required: they render in the footer of every page, in the privacy policy and on every invoice.">
        {site === null ? <div className="text-sm text-[#6B7280]">Loading…</div> : (
          <div className="space-y-4">
            <Field label="Company name" required data-testid="settings-company-name"
                   value={site.company_name || ""} placeholder="DharmaTN Legal LLP"
                   hint="The legal name. It prints in the footer of every page, in clause 1 of the privacy policy and on invoices — the Consumer Protection (E-Commerce) Rules 2020 require it to be displayed."
                   onChange={e => setSite(s => ({ ...s, company_name: e.target.value }))}/>
            <AreaField label="Registered address" required data-testid="settings-address"
                       value={site.address || ""}
                       placeholder={"12 Anna Salai\nChennai 600002\nTamil Nadu"}
                       hint="The principal geographic address, which those same rules require to be displayed. Printed in the FROM block of every invoice (up to 3 lines are drawn), in the site footer and in the privacy policy."
                       onChange={e => setSite(s => ({ ...s, address: e.target.value }))}/>
            <Field label="Business email" type="email" required data-testid="settings-email"
                   value={site.support_email || ""} placeholder="admin@dharmatn.in"
                   hint="Shown in the site footer and on invoices, and the address the privacy policy sends data requests and grievances to. It should accept replies."
                   onChange={e => setSite(s => ({ ...s, support_email: e.target.value }))}/>
            <Field label="WhatsApp Business Number" required data-testid="settings-whatsapp"
                   value={site.whatsapp || ""} placeholder="919876543210"
                   hint="Country code and number, digits only — punctuation is stripped on save. One number: it dials the public site's WhatsApp link and prints on invoices."
                   onChange={e => setSite(s => ({ ...s, whatsapp: e.target.value }))}/>
            <SaveBar id="company"/>
          </div>
        )}
      </Panel>

      <Panel icon={SlidersHorizontal} title="Limits"
             desc="What one customer, or one case, may consume. Each falls back to a default if left blank.">
        {site === null ? <div className="text-sm text-[#6B7280]">Loading…</div> : (
          <div className="space-y-4">
            <Field label="Maximum unfinished drafts per customer" type="number" min="1"
                   data-testid="settings-max-drafts"
                   value={site.max_drafts ?? ""} placeholder="5"
                   hint="A draft is the only record a customer can create without paying, so it is the only one that can be created without limit. Raise this for a genuine bulk customer; blank or invalid falls back to 5."
                   onChange={e => setSite(s => ({ ...s, max_drafts: e.target.value }))}/>
            <Field label="Maximum file size per attachment (MB)" type="number" min="1" max={site.max_upload_mb_ceiling ?? 100}
                   data-testid="settings-max-upload-mb"
                   value={site.max_upload_mb ?? ""} placeholder="100"
                   hint={`Applies to every attachment a customer, admin or reviewer uploads on a case — documents, reports and the issued soft copy. Cannot exceed ${site.max_upload_mb_ceiling ?? 100} MB, the hard ceiling this server is configured with.`}
                   onChange={e => setSite(s => ({ ...s, max_upload_mb: e.target.value }))}/>
            <Field label="Maximum attachments per person, per case" type="number" min="1" max="10"
                   data-testid="settings-max-attachments"
                   value={site.max_attachments_per_uploader ?? ""} placeholder="2"
                   hint="Each of the customer, the admin and the reviewer may attach up to this many files to a case — counted separately per person, so one party filling their quota doesn't block the others. Does not limit the soft copy, which already allows only one current copy at a time."
                   onChange={e => setSite(s => ({ ...s, max_attachments_per_uploader: e.target.value }))}/>
            <SaveBar id="limits"/>
          </div>
        )}
      </Panel>

      {/* Its own card, like Document protection: this is a notification channel, not a company
          detail. Only the mute switch is here — whether alerts can be sent at all is
          TELEGRAM_BOT_TOKEN/TELEGRAM_CHAT_ID in the deployment's environment, because a token is a
          credential and settings are rows every backup captures. */}
      {/* One line per option. The reasoning — what the message does and does not carry, why the
          token is not editable here, why a heartbeat is not downtime alerting — lives in
          deploy/TELEGRAM.md. A settings page is for choosing, not for reading. */}
      <Panel icon={Send} title="Telegram alerts"
             desc="Payment alerts and health reports to your team's chat. Setup: deploy/TELEGRAM.md">
        {site === null ? <div className="text-sm text-[#6B7280]">Loading…</div> : (
          <div className="space-y-4">
            <div>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" data-testid="settings-telegram-alerts" className="mt-1"
                       checked={site.telegram_alerts !== false}
                       onChange={e => setSite(s => ({ ...s, telegram_alerts: e.target.checked }))}/>
                <span className="text-sm text-white/90">Alert on payment</span>
              </label>
              <Help>
                A message when a customer pays and a case is ready to start. Carries the invoice
                number, document type, amount and a link — no customer name, email, number or
                address, because a group chat's history outlives its membership. Turning this off
                silences the chat without touching the bot token; nothing is queued while it is off.
                The token and chat id live in the deployment's environment, not here.
              </Help>
            </div>
            <div>
              <SettingLabel label="Health report" hint={
                "A line to the same chat confirming the backend, database and public site all "
                + "answer. It cannot tell you when you are down: a report the backend sends about "
                + "itself stops arriving if the backend stops. For that, point an external "
                + "monitor at /api/health/ready. See deploy/TELEGRAM.md."}/>
              <select
                data-testid="settings-health-hours"
                value={site.health_report_hours ?? 12}
                onChange={e => setSite(s => ({ ...s, health_report_hours: Number(e.target.value) }))}
                className="mt-1.5 w-full bg-[#080C16] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#CBA96C]/50"
              >
                <option value={0}>Off</option>
                {[2, 4, 6, 12].map(h => <option key={h} value={h}>Every {h} hours</option>)}
              </select>
            </div>
            {/* Send one now. The scheduled report is hours apart, so without this the only way to
                find out whether the token, the chat id and the bot's membership of the group are
                all still good is to wait — or to pay for a document and watch for the payment
                alert. The result is shown here rather than as a toast: a toast is gone before you
                have read why nothing was sent, and the whole value of this button is the why. */}
            <div className="pt-1 border-t border-white/5 space-y-2">
              <div className="flex items-center gap-3">
                <Button data-testid="settings-health-check" variant="outline" size="sm"
                        disabled={checking} onClick={runHealthCheck}>
                  <Activity className="w-3.5 h-3.5 mr-1.5"/>{checking ? "Checking…" : "Check health"}
                </Button>
                <span className="text-xs text-[#6B7280]">
                  Sends the health report to the chat now.
                </span>
              </div>
              {health && (
                <div data-testid="settings-health-result"
                     className={`rounded-lg border px-3 py-2.5 text-xs leading-relaxed ${
                       health.sent ? "border-[#4ADE80]/30 bg-[#4ADE80]/5 text-[#86EFAC]"
                                   : "border-[#F5A524]/40 bg-[#F5A524]/10 text-[#F5A524]"}`}>
                  <div className="font-medium">{HEALTH_OUTCOMES[health.reason] || HEALTH_OUTCOMES.sent}</div>
                  {/* What was (or would have been) sent, exactly as the chat receives it. */}
                  <pre className="mt-1.5 whitespace-pre-wrap font-sans text-[#D1D5DB]">{health.report}</pre>
                </div>
              )}
            </div>
            <SaveBar id="telegram"/>
          </div>
        )}
      </Panel>

      {/* Its own card. Two separate things live here and the copy has to keep them apart, because
          an admin who conflates them will think customer files are protected when only one half is
          on — which is exactly what burying this under "Company details" invited. */}
      <Panel icon={ShieldCheck} title="Document protection"
             desc="Who can open the files this platform stores, and the files a customer takes away.">
        {site === null ? <div className="text-sm text-[#6B7280]">Loading…</div> : (
          <div className="space-y-4">
            <div>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" data-testid="settings-pdf-lock" className="mt-1"
                       checked={site.pdf_lock !== false}
                       onChange={e => setSite(s => ({ ...s, pdf_lock: e.target.checked }))}/>
                <span className="text-sm text-white/90">Password-protect customer downloads</span>
              </label>
              <Help>
                Two separate things, and they are easy to confuse. Files <em>on this server</em> are
                already encrypted at rest with a key in the environment — that is what protects them
                if a backup or disk is taken. This setting is the other half: a password on the
                documents a customer downloads, protecting the file after it leaves us. It applies
                to case documents and attachments; the tax invoice is deliberately left unlocked,
                being a financial record customers forward to accountants and landlords.
              </Help>
            </div>
            {site.pdf_lock !== false && (
              <div className="md:col-span-2">
                <Field label="Document password (admin)" data-testid="settings-pdf-password"
                       value={site.pdf_password ?? ""} placeholder="Dharmatn@007"
                       onChange={e => setSite(s => ({ ...s, pdf_password: e.target.value }))}
                       hint={null}/>
<Help>
                  <strong className="text-white/90">Two passwords open a locked document, and a PDF
                  allows no more than two.</strong> First, the customer's registered mobile number,
                  digits only without the country code — that is what they are told to use. Second,
                  this one: your team's, for support to open a document on the phone with them.
                  Never shown to a customer and never published to the public site settings.
                  Minimum 6 characters, and it cannot be blank while the lock is on.
                </Help>
              </div>
            )}
            <SaveBar id="documents"/>
          </div>
        )}
      </Panel>

      <Panel icon={LayoutList} title="Display"
             desc="How much the tables show, and the window the ranged pages open on. Applies to both portals.">
        {site === null ? <div className="text-sm text-[#6B7280]">Loading…</div> : (
          <div className="space-y-4">
            <div>
              <SettingLabel label="Rows per page" hint={
                "Every table in both portals. These lists only grow — transactions, orders and "
                + "payments are never deleted — so a page that rendered all of them would get "
                + "slower every week. 10 is the default."}/>
              <select
                data-testid="settings-page-size"
                value={site.page_size ?? 10}
                onChange={e => setSite(s => ({ ...s, page_size: Number(e.target.value) }))}
                className="mt-1.5 w-full bg-[#080C16] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#CBA96C]/50"
              >
                {PAGE_SIZE_CHOICES.map(n => <option key={n} value={n}>{n} per page</option>)}
              </select>
            </div>
            <div>
              <SettingLabel label="Default date range" hint={
                "The window Dashboard, Transactions and Payments open on. One setting, because "
                + "those pages are read against each other and a dashboard showing the month "
                + "beside a queue showing the week is a comparison nobody makes correctly. Each "
                + "page can still be widened on the spot. 30 days is the default."}/>
              <select
                data-testid="settings-range-days"
                value={site.default_range_days ?? 30}
                onChange={e => setSite(s => ({ ...s, default_range_days: Number(e.target.value) }))}
                className="mt-1.5 w-full bg-[#080C16] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#CBA96C]/50"
              >
                {RANGE_DAYS_CHOICES.map(n => <option key={n} value={n}>Last {n} days</option>)}
              </select>
            </div>
            <SaveBar id="display"/>
          </div>
        )}
      </Panel>

      <Panel icon={ScrollText} title="Logging"
             desc="How much detail the backend writes to its log. Applied immediately on save — no restart.">
        {logInfo === null ? <div className="text-sm text-[#6B7280]">Loading…</div> : (
          <div className="space-y-4">
            <div>
              <SettingLabel label="Backend log level" hint={
                "INFO is the normal level. DEBUG adds a line for every step of every request — "
                + "turn it on to chase a problem, then back down: it is noisy and costs storage. "
                + "WARNING and ERROR show only problems. Not saved permanently: a restart of the "
                + "backend returns it to INFO (or LOG_LEVEL in the environment). See LOGGING.md."}/>
              <select
                data-testid="settings-log-level"
                value={logLevel}
                onChange={e => setLogLevel(e.target.value)}
                className="mt-1.5 w-full bg-[#080C16] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#CBA96C]/50"
              >
                {(logInfo.available?.length ? logInfo.available : ["DEBUG", "INFO", "WARNING", "ERROR"])
                  .map(l => <option key={l} value={l}>{l}{l === (logInfo.default || "INFO") ? " (default)" : ""}</option>)}
              </select>
              {logInfo.current?.dharmatn && (
                <div className="text-[11px] text-[#6B7280] mt-1.5" data-testid="settings-log-level-current">
                  Running at <b className="text-[#A0AABF]">{logInfo.current.dharmatn}</b>
                  {logInfo.default && <> · returns to <b className="text-[#A0AABF]">{logInfo.default}</b> on restart</>}
                </div>
              )}
            </div>
            <Button data-testid="settings-save-log-level" disabled={logBusy} onClick={saveLogLevel}
                    className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
              {logBusy ? "Applying…" : "Apply log level"}
            </Button>
          </div>
        )}
      </Panel>

      <Panel icon={ReceiptText} title="Invoice template"
             desc="The header and footer of the tax invoice emailed to a customer after payment. Applies to invoices generated from now on — bills already issued keep the wording they were created with.">
        {bill === null ? <div className="text-sm text-[#6B7280]">Loading…</div> : (
          <div className="space-y-4">
            <Field label="Tagline" data-testid="bill-tagline"
                   value={bill.tagline || ""} placeholder="Legal Services · Chennai, Tamil Nadu"
                   hint="The small line under the company name in the header."
                   onChange={e => setBill(b => ({ ...b, tagline: e.target.value }))}/>
            <Field label="Document title" data-testid="bill-title"
                   value={bill.title || ""} placeholder="TAX INVOICE"
                   hint="Printed top-right. Use TAX INVOICE only if you are GST-registered and the GSTIN below is filled in — otherwise INVOICE or RECEIPT."
                   onChange={e => setBill(b => ({ ...b, title: e.target.value }))}/>
            <div className="rounded-lg border border-white/10 p-3.5 space-y-3">
              {/* The icon sits *outside* the <label>: a button inside a label is activated by the
                  label, so opening the help would silently toggle GST. */}
              <div className="flex items-center gap-1">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox" data-testid="bill-gst-toggle" checked={gstOn}
                    onChange={e => toggleGst(e.target.checked)}
                    className="w-4 h-4 accent-[#CBA96C]"/>
                  <span className="text-sm text-white">Registered for GST</span>
                </label>
                <Help>
                  The single switch for the whole tax presentation. On, prices split into
                  Subtotal / GST / Grand Total everywhere — price list, cart, payment and invoice —
                  and the bill prints a GST No line. Off, no tax is charged or shown anywhere, and
                  your per-document GST rates are kept for whenever you register.
                </Help>
              </div>
              {gstOn && (
                <Field label="GSTIN" data-testid="bill-gstin" required
                       value={bill.gstin || ""} placeholder="33AAAAA0000A1Z5"
                       hint="15 characters: state code, PAN, entity digit, Z, check character. Printed on every invoice and quoted to the tax authority, so it is checked for shape before saving. Required while GST is on."
                       onChange={e => setBill(b => ({ ...b, gstin: e.target.value.toUpperCase() }))}/>
              )}
              {/* "TAX INVOICE" on a bill that charges no tax is the wrong word. Warned, not
                  rewritten: the title is the admin's wording and silently editing it would be a
                  worse surprise than being told. */}
              {!gstOn && /tax/i.test(bill.title || "") && (
                <div className="text-[11px] text-[#F5A524]" data-testid="bill-title-warning">
                  The document title still says {'"'}{bill.title}{'"'} while GST is off. Consider
                  {' "'}INVOICE{'"'} or {'"'}RECEIPT{'"'} — a bill that itemises no tax should not
                  call itself a tax invoice.
                </div>
              )}
            </div>
            <AreaField label="Footer" data-testid="bill-footer"
                       value={bill.footer || ""}
                       hint={`Terms, a thank-you, or a payment note. Up to ${billLimits.footer_lines || 3} lines are drawn — the invoice has no second page, so anything beyond that is not printed.`}
                       onChange={e => setBill(b => ({ ...b, footer: e.target.value }))}/>
            <div className="text-[11px] text-[#6B7280] border-t border-white/5 pt-3">
              The FROM block comes from <b className="text-[#A0AABF]">Company details</b>; line-item
              amounts come from <b className="text-[#A0AABF]">Pricing</b>.
              <Help>
                Company name, registered address, email and WhatsApp number are entered once in
                Company details and printed on every invoice from there, so the site and the invoice
                cannot drift apart. The line-item table is not editable here: its amounts are what
                was actually charged.
              </Help>
            </div>
            <Button data-testid="bill-save" disabled={busy} onClick={saveBill}
                    className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
              {busy ? "Saving…" : "Save invoice template"}
            </Button>
          </div>
        )}
      </Panel>

      <Panel icon={Truck} title="Delivery partners"
             desc="Couriers used for dispatch. Pick one when updating a case's delivery status and its name and tracking URL fill in automatically.">
        {partners && (
          <div className="space-y-3">
            {partners.map((p, i) => (
              <div key={p.id || `new-${i}`} className="grid sm:grid-cols-[1fr_1fr_1.4fr_auto] gap-2 items-start bg-[#080C16] border border-white/10 rounded-lg p-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280]">Name</Label>
                  <Input data-testid={`partner-name-${i}`} value={p.name} placeholder="BlueDart Express"
                         onChange={e => updatePartner(i, { name: e.target.value })}
                         className="mt-1 h-9 text-sm bg-[#111623] border-white/10 text-white"/>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280]">Contact</Label>
                  <Input data-testid={`partner-contact-${i}`} value={p.contact} placeholder="Phone or email"
                         onChange={e => updatePartner(i, { contact: e.target.value })}
                         className="mt-1 h-9 text-sm bg-[#111623] border-white/10 text-white"/>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-[#6B7280]">Tracking URL</Label>
                  <Input data-testid={`partner-url-${i}`} value={p.url_template} placeholder="https://partner.example/track?id=<Tracking-Id>"
                         onChange={e => updatePartner(i, { url_template: e.target.value })}
                         className="mt-1 h-9 text-sm bg-[#111623] border-white/10 text-white font-mono"/>
                </div>
                <Button data-testid={`partner-remove-${i}`} variant="ghost" size="icon" onClick={() => removePartner(i)}
                        className="mt-5 text-[#A0AABF] hover:text-[#F27373]" aria-label={`Remove ${p.name || "partner"}`}>
                  <Trash2 className="w-4 h-4"/>
                </Button>
              </div>
            ))}
            <div className="text-[11px] text-[#6B7280]">
              Use <code className="text-[#CBA96C]">&lt;Tracking-Id&gt;</code> anywhere in the URL — it's
              replaced with the tracking ID entered on a case's delivery update.
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button data-testid="partner-add" variant="outline" onClick={addPartner}
                      className="bg-transparent border-white/20 text-white hover:bg-white/5">
                <Plus className="w-4 h-4 mr-1.5"/>Add partner
              </Button>
              <Button data-testid="partners-save" disabled={partnersBusy} onClick={savePartners}
                      className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
                {partnersBusy ? "Saving…" : "Save delivery partners"}
              </Button>
            </div>
          </div>
        )}
      </Panel>

      <Panel icon={KeyRound} title="Change password"
             desc="Your current password is required — an open session should not be enough to lock you out of your own account.">
        <div className="space-y-4">
          <Field label="Current password" type="password" data-testid="settings-current-pw"
                 value={pw.current_password} autoComplete="current-password"
                 onChange={e => setPw(p => ({ ...p, current_password: e.target.value }))}/>
          <Field label="New password" type="password" data-testid="settings-new-pw"
                 value={pw.new_password} autoComplete="new-password" hint="At least 8 characters."
                 onChange={e => setPw(p => ({ ...p, new_password: e.target.value }))}/>
          <Field label="Confirm new password" type="password" data-testid="settings-confirm-pw"
                 value={pw.confirm} autoComplete="new-password"
                 onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))}/>
          <Button data-testid="settings-save-pw" disabled={busy || !pw.current_password || !pw.new_password}
                  onClick={changePassword} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">
            {busy ? "Saving…" : "Change password"}
          </Button>
        </div>
      </Panel>
    </div>
    </TooltipProvider>
  );
}
